import type { MessageKey } from "@erp/i18n";
import type { Permission } from "@erp/shared";
import { createFileRoute, Link, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { Page, PageHeader } from "~/components/ui/page";
import { cn } from "~/lib/cn";
import { useT } from "~/lib/i18n";
import { useCan } from "~/lib/session";

export const Route = createFileRoute("/_app/settings")({ component: SettingsLayout });

type SettingsPath =
  | "/settings"
  | "/settings/company"
  | "/settings/documents"
  | "/settings/sales"
  | "/settings/families"
  | "/settings/stock"
  | "/settings/shipping"
  | "/settings/printing"
  | "/settings/sav"
  | "/settings/purchasing"
  | "/settings/users"
  | "/settings/integrations"
  | "/settings/imports"
  | "/settings/activity"
  | "/settings/health";

interface SettingsGroup {
  label: MessageKey;
  items: { to: SettingsPath; label: MessageKey; permission?: Permission }[];
}

/** Settings by business area: each setting has one obvious place. */
const GROUPS: SettingsGroup[] = [
  { label: "settings.groups.account", items: [{ to: "/settings", label: "settings.pages.account.title" }] },
  {
    label: "settings.groups.company",
    items: [
      { to: "/settings/company", label: "settings.pages.company.title", permission: "sales:read" },
      { to: "/settings/documents", label: "settings.pages.documents.title", permission: "sales:read" },
    ],
  },
  {
    label: "settings.groups.sales",
    items: [{ to: "/settings/sales", label: "settings.pages.sales.title", permission: "sales:read" }],
  },
  {
    label: "settings.groups.catalog",
    items: [
      { to: "/settings/families", label: "settings.pages.families.title", permission: "products:read" },
      { to: "/settings/stock", label: "settings.pages.stock.title", permission: "stock:read" },
    ],
  },
  {
    label: "settings.groups.purchasing",
    items: [{ to: "/settings/purchasing", label: "settings.pages.purchasing.title", permission: "purchasing:read" }],
  },
  {
    label: "settings.groups.logistics",
    items: [
      { to: "/settings/shipping", label: "settings.pages.shipping.title", permission: "stock:read" },
      { to: "/settings/printing", label: "settings.pages.printing.title", permission: "settings:manage" },
      { to: "/settings/sav", label: "settings.pages.sav.title", permission: "sav:read" },
    ],
  },
  {
    label: "settings.groups.admin",
    items: [
      { to: "/settings/users", label: "settings.pages.users.title", permission: "users:manage" },
      { to: "/settings/integrations", label: "settings.pages.integrations.title", permission: "settings:manage" },
      { to: "/settings/imports", label: "settings.pages.imports.title", permission: "imports:run" },
      { to: "/settings/activity", label: "settings.pages.activity.title", permission: "audit:read" },
      { to: "/settings/health", label: "settings.pages.health.title", permission: "settings:manage" },
    ],
  },
];

function SettingsLayout() {
  const t = useT();
  const can = useCan();
  const navigate = useNavigate();
  const pathname = useLocation({ select: (l) => l.pathname.replace(/\/$/, "") || "/settings" });
  const groups = GROUPS.map((g) => ({ ...g, items: g.items.filter((i) => !i.permission || can(i.permission)) })).filter(
    (g) => g.items.length > 0,
  );
  const items = groups.flatMap((g) => g.items);
  // The document template page shows a PDF preview beside its form: it gets the full width.
  const wide = pathname.endsWith("/settings/documents");
  return (
    <Page className={cn("max-w-6xl", wide && "max-w-[96rem]")}>
      <PageHeader title={t("settings.title")} />
      {items.length > 1 ? (
        <div className="md:grid md:grid-cols-[200px_minmax(0,1fr)] md:gap-8">
          {/* Phone: one grouped menu above the page. */}
          <label className="mb-5 block md:hidden">
            <span className="sr-only">{t("settings.sections")}</span>
            <select
              value={items.find((i) => i.to === pathname)?.to ?? "/settings"}
              onChange={(e) => void navigate({ to: e.target.value as SettingsPath })}
              className="h-11 w-full rounded-control border border-line bg-surface px-3 text-sm"
            >
              {groups.map((g) => (
                <optgroup key={g.label} label={t(g.label)}>
                  {g.items.map((i) => (
                    <option key={i.to} value={i.to}>
                      {t(i.label)}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
          <nav aria-label={t("settings.sections")} className="hidden md:block">
            <div className="sticky top-4 flex flex-col gap-4">
              {groups.map((g) => (
                <div key={g.label}>
                  <p className="mb-1 px-3 text-[11px] font-medium tracking-wide text-fg-3 uppercase">{t(g.label)}</p>
                  <ul className="flex flex-col">
                    {g.items.map((i) => (
                      <li key={i.to}>
                        <Link
                          to={i.to}
                          activeOptions={{ exact: true }}
                          className="flex h-9 items-center rounded-control px-3 text-sm text-fg-2 transition-colors hover:bg-surface-2 hover:text-fg"
                          activeProps={{ className: "bg-surface-2 font-medium text-fg!", "aria-current": "page" }}
                        >
                          {t(i.label)}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </nav>
          <div className="min-w-0">
            <Outlet />
          </div>
        </div>
      ) : (
        <Outlet />
      )}
    </Page>
  );
}
